import { z } from 'zod';
import { invoiceIntakeFlagSchema } from './apIntake.js';
import { defined } from './defined.js';
import { dateStringValidation, documentReferenceSchema, optionalDateString } from './index.js';
import { llmOptional } from './rfp.js';
export const invoiceStatusSchema = z.enum([
    'unverified',
    'verified',
    'received',
    'pending-approval',
    'approved',
    'paid',
]);
export const qboPaidCheckOutcomeSchema = z.enum([
    'bill_missing',
    'needs_reauth',
    'open_dispute',
    'check_failed',
    'not_in_qbo',
    'bill_ambiguous',
]);
export const invoiceDataSchema = z.object({
    contractorName: z.string().min(1, 'Contractor name is required'),
    vendorEmail: llmOptional(z.string().email().max(255)),
    billingAddress: llmOptional(z.string().max(500)),
    billingContactName: llmOptional(z.string().max(255)),
    billingContactPhone: llmOptional(z.string().max(50)),
    billingEmail: llmOptional(z
        .string()
        .trim()
        .max(255)
        .pipe(z.union([z.literal(''), z.string().email()]))),
    officeAddress: llmOptional(z.string().max(500)),
    contactPersonName: llmOptional(z.string().max(255)),
    remittanceInfo: llmOptional(z.string()),
    invoiceNumber: z.string().optional(),
    invoiceDate: optionalDateString,
    invoiceAmount: z.number().optional(),
    amountCandidates: z
        .array(z.object({
        amount: z.number(),
        label: z.string(),
        kind: z.enum([
            'current-total',
            'subtotal',
            'tax',
            'freight',
            'line-item',
            'prior-balance',
            'balance-due',
            'amount-due',
            'payment',
            'adjustment',
            'other',
        ]),
        pageNumber: z.number().int().positive(),
        quote: z.string(),
        yPosition: z.number().optional(),
    }))
        .optional(),
    amountBreakdown: z
        .array(z.object({
        label: z.string(),
        amount: z.number(),
        kind: z.enum(['line-item', 'subtotal', 'tax', 'freight', 'adjustment']),
    }))
        .optional(),
    dueDate: optionalDateString,
    notes: z.string().optional(),
    references: z.array(documentReferenceSchema),
});
export function getInvoiceApprovalPolicy(params) {
    const { invoiceAmount, pmApprovalLimit, isAdmin } = params;
    if (invoiceAmount == null) {
        return { kind: 'no-amount' };
    }
    if (pmApprovalLimit == null) {
        return { kind: 'no-limit' };
    }
    if (invoiceAmount <= pmApprovalLimit) {
        return { kind: 'within-limit' };
    }
    if (isAdmin) {
        return { kind: 'admin-override' };
    }
    return { kind: 'requires-board-approval' };
}
export const paymentStrategySchema = z.enum([
    'unconfigured',
    'manual',
    'payabli',
    'autopayExternal',
]);
export const selectablePaymentStrategySchema = z.enum(['manual', 'payabli', 'autopayExternal']);
export const invoiceVendorIdentitySchema = z.discriminatedUnion('source', [
    z.strictObject({
        policyVersion: z.literal(1),
        source: z.literal('pending'),
        boundName: z.string(),
        pendingReason: z.enum(['possible-duplicate', 'missing-name']),
    }),
    z.strictObject({
        policyVersion: z.literal(1),
        source: z.literal('alias'),
        boundName: z.string(),
        aliasDisplayId: z.string(),
        vendorDisplayId: z.string(),
    }),
    z.strictObject({
        policyVersion: z.literal(1),
        source: z.enum(['exact', 'selected', 'created']),
        boundName: z.string(),
        vendorDisplayId: z.string(),
    }),
]);
export const invoiceVendorFieldSchema = z.enum([
    'contactEmail',
    'contactPersonName',
    'billingEmail',
    'billingContactName',
    'billingContactPhone',
    'officeAddress',
    'billingAddress',
    'remittanceInfo',
]);
export const invoiceVendorFieldChoicesSchema = z.partialRecord(invoiceVendorFieldSchema, z.enum(['stored', 'incoming']));
export const invoiceVendorDecisionSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('existing'),
        vendorDisplayId: z.string(),
        snapshotToken: z.string(),
        evidenceToken: z.string().optional(),
        fieldChoices: invoiceVendorFieldChoicesSchema.optional(),
        rememberVariant: z.boolean().default(false),
    }),
    z.object({ kind: z.literal('create') }),
    z.object({ kind: z.literal('retain') }),
    z.object({ kind: z.literal('clear') }),
]);
export const invoiceVendorDispositionSchema = z.enum([
    'filled',
    'updated',
    'retained',
    'conflict',
    'protected-blank',
    'invoice-only',
    'already-applied',
]);
export const invoiceVendorEnrichmentSummarySchema = z.array(z.object({
    field: invoiceVendorFieldSchema,
    disposition: invoiceVendorDispositionSchema,
}));
export const invoiceStatusMetadataSchema = z.object({
    contractAutoAssociationDisabled: z.boolean().optional(),
    vendorContactOverride: z.string().email().nullable().optional(),
    vendorEnrichmentApplications: z
        .array(z.object({
        evidenceFingerprint: z.string(),
        targetVendorDisplayId: z.string(),
        completed: z.literal(true),
        filledFields: z.array(invoiceVendorFieldSchema),
    }))
        .optional(),
    approvalVendorSnapshot: z
        .object({ policyVersion: z.literal(1), vendorDisplayId: z.string(), vendorName: z.string() })
        .optional(),
    approvalRouting: z
        .object({
        approvers: z
            .array(z.object({
            id: z.string(),
            name: z.string(),
        }))
            .min(1)
            .max(2),
    })
        .optional(),
    approvalDecision: z
        .object({
        approvedByUserDisplayId: z.string(),
        approvedByUserName: z.string(),
        approvedAt: z.string(),
    })
        .optional(),
    denialDecision: z
        .object({
        deniedByUserDisplayId: z.string(),
        deniedByUserName: z.string(),
        deniedAt: z.string(),
        denialReason: z.string().optional(),
    })
        .optional(),
    mismatchAcknowledgement: z
        .object({
        justification: z.string(),
        acknowledgedAt: dateStringValidation,
        source: z.enum(['pm', 'external_autopay']).default('pm'),
    })
        .optional(),
    contractFlags: z.array(z.string()).optional(),
    duplicateFlag: z
        .object({
        matchingInvoiceIds: z.array(z.string()),
    })
        .optional(),
    duplicateDismissed: z.boolean().optional(),
    intakeFlags: z.array(invoiceIntakeFlagSchema).optional(),
    expenseAccountId: z.string().optional(),
    taxonomyId: z.number().optional(),
    pendingContactEmail: z.string().email().optional(),
    paymentStrategySnapshot: selectablePaymentStrategySchema.optional(),
});
export function storedExpenseAccountId(metadata) {
    const id = metadata?.expenseAccountId;
    return id === undefined || id.trim() === '' ? undefined : id;
}
export function expenseAccountForCategory(metadata, taxonomyId) {
    const stored = storedExpenseAccountId(metadata);
    if (stored === undefined)
        return undefined;
    const savedUnder = metadata?.taxonomyId;
    return savedUnder === undefined || savedUnder === taxonomyId ? stored : undefined;
}
function metadataWithRuledCategory(metadata, taxonomyId) {
    if (taxonomyId === undefined)
        return metadata;
    const { taxonomyId: _savedUnder, expenseAccountId: _account, ...rest } = metadata;
    const next = taxonomyId ?? undefined;
    return {
        ...rest,
        ...defined({ taxonomyId: next, expenseAccountId: expenseAccountForCategory(metadata, next) }),
    };
}
function metadataWithSentAccount(metadata, expenseAccountId) {
    if (expenseAccountId === undefined)
        return metadata;
    const { expenseAccountId: _account, ...rest } = metadata;
    return expenseAccountId === null ? rest : { ...rest, expenseAccountId };
}
export function withCategory(metadata, { taxonomyId, expenseAccountId }) {
    const ruled = metadataWithRuledCategory(metadata, taxonomyId);
    const stored = storedExpenseAccountId(metadata);
    const ruleRemovedIt = expenseAccountId === undefined && storedExpenseAccountId(ruled) === undefined;
    return {
        metadata: metadataWithSentAccount(ruled, expenseAccountId),
        droppedExpenseAccountId: ruleRemovedIt ? stored : undefined,
    };
}
export function isExternalAutoPay(metadata) {
    return metadata?.mismatchAcknowledgement?.source === 'external_autopay';
}
export function contractValidationScope(status) {
    switch (status) {
        case 'verified':
            return 'match';
        case 'received':
        case 'pending-approval':
            return 'clear_stale_autopay';
        case 'unverified':
        case 'approved':
        case 'paid':
            return 'none';
    }
}
export function contractMatchDisplay(invoice) {
    if (invoice.contractId)
        return { kind: 'matched', contractId: invoice.contractId };
    if (isExternalAutoPay(invoice.metadata))
        return { kind: 'external_autopay' };
    if (invoice.status === 'verified' && !invoice.metadata?.contractFlags?.length) {
        return { kind: 'matching' };
    }
    return { kind: 'no_contract' };
}
export const payoutCancelOriginSchema = z.discriminatedUnion('by', [
    z.object({ by: z.literal('manager') }),
    z.object({ by: z.literal('payabli'), event: z.string().min(1) }),
    z.object({ by: z.literal('reconcile'), reason: z.string().min(1) }),
]);
const paymentAttemptStatusVariants = [
    z.object({ kind: z.literal('scheduling') }),
    z.object({
        kind: z.literal('authorizing'),
        payabliBillId: z.number().int().positive().optional(),
        linkSentAt: z.string().datetime().optional(),
    }),
    z.object({
        kind: z.literal('awaitingVendorEnrollment'),
        payabliBillId: z.number().int().positive(),
        linkSentAt: z.string().datetime(),
    }),
    z.object({
        kind: z.literal('inTransit'),
        payabliTransId: z.string().min(1),
        payabliBillId: z.number().int().positive().optional(),
    }),
    z.object({ kind: z.literal('confirmed'), payabliTransId: z.string().min(1).nullable() }),
    z.object({
        kind: z.literal('failed'),
        payabliTransId: z.string().min(1).nullable(),
        reason: z.string(),
    }),
    z.object({ kind: z.literal('awaitingExternalConfirmation') }),
    z.object({ kind: z.literal('withdrawn') }),
    z.object({
        kind: z.literal('canceled'),
        payabliTransId: z.string().min(1).nullable(),
        payabliBillId: z.number().int().positive().optional(),
        origin: payoutCancelOriginSchema,
    }),
];
export const paymentAttemptStatusSchema = z.discriminatedUnion('kind', paymentAttemptStatusVariants);
export const paymentAttemptKindSchema = z.enum(paymentAttemptStatusVariants.map((variant) => variant.shape.kind.value));
export const ledgerPostingStatusSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('notStarted') }),
    z.object({ kind: z.literal('posting') }),
    z.object({ kind: z.literal('posted'), qboBillPaymentId: z.string().min(1) }),
    z.object({ kind: z.literal('syncFailed'), qboError: z.string() }),
]);
//# sourceMappingURL=invoices.js.map